import { forwardRef, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';

// material-ui
import { Avatar, Chip, ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// project imports
import LAYOUT_CONST from 'constant';
import useConfig from 'hooks/useConfig';
import { useDispatch, useSelector } from 'store';
import { activeID, activeItem, openDrawer } from 'store/slices/menu';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useIntl } from 'react-intl';
import { NavItemType } from 'types';

// focus
import { useFocusMainContent } from 'hooks/useFocusMainContent';

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

interface NavItemProps {
    item: NavItemType;
    level: number;
    parentId: string;
}

const NavItem = ({ item, level, parentId }: NavItemProps) => {
    const intl = useIntl();
    const theme = useTheme();
    const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));

    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const { layout, borderRadius } = useConfig();

    const { selectedItem, drawerOpen } = useSelector((state) => state.menu);
    const isSelected = selectedItem.findIndex((id) => id === item.id) > -1;

    const textColor = theme.palette.mode === 'dark' ? 'grey.400' : 'grey.900';
    const iconSelectedColor = theme.palette.mode === 'dark' && drawerOpen ? 'text.primary' : theme.palette.primary.dark;

    const focusMainContent = useFocusMainContent();

    // Foco automático al primer elemento
    const focusRef = useCallback(
        (node: HTMLDivElement) => {
            if (node && item.id === 'esign-signdocument') {
                node.focus();
            }
        },
        [item.id]
    );

    const itemHandler = (id: string) => {
        dispatch(activeItem([id]));
        if (matchesSM) dispatch(openDrawer(false));
        dispatch(activeID(parentId));
        focusMainContent();
    };

    const Icon = item?.icon!;
    const itemIcon = item?.icon ? (
        <Icon aria-hidden="true" sx={{ fontSize: '20px', color: isSelected ? iconSelectedColor : theme.palette.grey[900] }} />
    ) : (
        <FiberManualRecordIcon
            sx={{
                color: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
                width: selectedItem.findIndex((id) => id === item?.id) > -1 ? 8 : 6,
                height: selectedItem.findIndex((id) => id === item?.id) > -1 ? 8 : 6
            }}
            fontSize={level > 0 ? 'inherit' : 'medium'}
        />
    );

    const typographyColor = isSelected ? theme.palette.primary.dark : undefined;

    const itemTarget = item.target ? '_blank' : '_self';

    const listItemProps = useMemo(() => {
        if (item?.external) {
            return { component: 'a', href: item.url, target: itemTarget };
        }
        return {
            component: forwardRef<HTMLAnchorElement, any>((props, ref) => <Link ref={ref} {...props} to={item.url!} target={itemTarget} />)
        };
    }, [item.external, item.url, itemTarget]);

    useEffect(() => {
        const currentIndex = document.location.pathname
            .toString()
            .split('/')
            .findIndex((id) => id === item.id);
        if (currentIndex > -1) {
            dispatch(activeItem([item.id]));
        } else if (isSelected) {
            dispatch(activeItem([]));
        }
        // eslint-disable-next-line
    }, [pathname]);

    return (
        <>
            {layout === LAYOUT_CONST.VERTICAL_LAYOUT || (layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && matchDownMd) ? (
                <ListItem
                    disablePadding
                    sx={{
                        width: '100%',
                        display: 'block'
                    }}
                >
                    {/* Tooltip for the item */}
                    <Tooltip
                        title={intl.formatMessage({ id: item.title as string })}
                        placement="right"
                        disableHoverListener={drawerOpen}
                        disableFocusListener={drawerOpen || item.id === 'esign-signdocument'}
                    >
                        <ListItemButton
                            {...listItemProps}
                            ref={focusRef}
                            id={`menu-item-${item.id}`}
                            aria-current={isSelected ? 'page' : undefined}
                            disabled={item.disabled}
                            disableRipple={!drawerOpen}
                            aria-label={intl.formatMessage({ id: item.title as string })}
                            sx={{
                                width: '100%',
                                zIndex: 1201,
                                borderRadius: `${borderRadius}px`,
                                mb: 0.5,
                                p: '5px',
                                pl: drawerOpen ? `${level * 8}px` : 1.25,
                                ...(drawerOpen &&
                                    level === 1 &&
                                    theme.palette.mode !== 'dark' && {
                                        '&:hover': {
                                            background: theme.palette.primary.light
                                        },
                                        '&.Mui-selected': {
                                            background: theme.palette.primary.light,
                                            color: iconSelectedColor,
                                            '&:hover': {
                                                color: iconSelectedColor,
                                                background: theme.palette.primary.light
                                            }
                                        }
                                    }),
                                ...((!drawerOpen || level !== 1) && {
                                    py: level === 1 ? 0 : 1,
                                    '&:hover': {
                                        bgcolor: 'transparent'
                                    },
                                    '&.Mui-selected': {
                                        '&:hover': {
                                            bgcolor: 'transparent'
                                        },
                                        bgcolor: 'transparent'
                                    }
                                })
                            }}
                            selected={isSelected}
                            onClick={() => itemHandler(item.id!)}
                        >
                            <ListItemIcon
                                aria-hidden="true"
                                sx={{
                                    minWidth: level === 1 ? 36 : 18,
                                    color: isSelected ? iconSelectedColor : textColor,
                                    ...(!drawerOpen &&
                                        level === 1 && {
                                            borderRadius: `${borderRadius}px`,
                                            width: 36,
                                            height: 36,
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            '&:hover': {
                                                bgcolor: theme.palette.mode === 'dark' ? theme.palette.primary.main + 25 : 'primary.light'
                                            },
                                            ...(isSelected && {
                                                bgcolor: theme.palette.mode === 'dark' ? theme.palette.primary.main + 25 : 'primary.light',
                                                '&:hover': {
                                                    bgcolor:
                                                        theme.palette.mode === 'dark' ? theme.palette.primary.main + 30 : 'primary.light'
                                                }
                                            })
                                        })
                                }}
                            >
                                {itemIcon}
                            </ListItemIcon>

                            {(drawerOpen || (!drawerOpen && level !== 1)) && (
                                <ListItemText
                                    primary={
                                        <Typography
                                            variant={isSelected ? 'h5' : 'body1'}
                                            fontSize="14px"
                                            lineHeight="1.3rem"
                                            color={typographyColor}
                                        >
                                            {intl.formatMessage({ id: item.title as string })}
                                        </Typography>
                                    }
                                    secondary={
                                        item.caption && (
                                            <Typography
                                                variant="caption"
                                                sx={{ ...theme.typography.subMenuCaption }}
                                                display="block"
                                                gutterBottom
                                                fontSize="14px"
                                                color={typographyColor}
                                            >
                                                {item.caption}
                                            </Typography>
                                        )
                                    }
                                />
                            )}
                        </ListItemButton>
                    </Tooltip>
                </ListItem>
            ) : (
                <ListItem disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                        ref={focusRef}
                        id={`menu-item-${item.id}`}
                        disabled={item.disabled}
                        sx={{
                            borderRadius: 0,
                            mb: 0.5,
                            alignItems: 'flex-start',
                            backgroundColor: level > 1 ? 'transparent !important' : 'inherit',
                            py: 1,
                            pl: 2
                        }}
                        selected={isSelected}
                        onClick={() => itemHandler(item.id!)}
                    >
                        <ListItemIcon
                            sx={{
                                my: 'auto',
                                minWidth: !item?.icon ? 24 : 36
                            }}
                        >
                            {itemIcon}
                        </ListItemIcon>

                        <ListItemText
                            primary={
                                <Typography variant={isSelected ? 'h5' : 'body1'} color={typographyColor}>
                                    {item.title}
                                </Typography>
                            }
                            secondary={
                                item.caption && (
                                    <Typography variant="caption" sx={{ ...theme.typography.subMenuCaption }} display="block" gutterBottom>
                                        {item.caption}
                                    </Typography>
                                )
                            }
                        />

                        {item.chip && (
                            <Chip
                                color={item.chip.color}
                                variant={item.chip.variant}
                                size={item.chip.size}
                                label={item.chip.label}
                                avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
                            />
                        )}
                    </ListItemButton>
                </ListItem>
            )}
        </>
    );
};

export default NavItem;
